import { useEffect, type ReactElement } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setSelectedCenter, useGetCentersQuery } from '../../../services/ConfigurationService';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasLaborManagementAccess } from '../../profile';

interface OwnCenter {
  code: string;
  name: string;
}

const findOwnCenter = (ownCenters: OwnCenter[], code: string | null): OwnCenter | undefined =>
  ownCenters.find((access) => access.code.toLowerCase() === code?.toLowerCase());

/** A link to a center the user holds also becomes the facility box's center, so the two agree. */
function useFacilityBoxSync(linked: OwnCenter | undefined): void {
  const dispatch = useAppDispatch();
  const selectedCenter = useAppSelector((state) => state.center.selectedCenter);
  useEffect(() => {
    if (linked && linked.code !== selectedCenter) dispatch(setSelectedCenter(linked.code));
  }, [linked, selectedCenter, dispatch]);
}

/**
 * The "no access" message for a link whose center is outside Center Access. The code alone means
 * nothing to a center administrator, so the link's center is named from the Configuration
 * catalogue (the profile cannot name a center it does not hold), the user's own centers are listed
 * by name, and a button opens the same page on the center the facility box shows.
 */
function ForeignCenterNotice({ center, ownCenters }: { center: string; ownCenters: OwnCenter[] }): ReactElement {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCenter = useAppSelector((state) => state.center.selectedCenter);
  const company = searchParams.get('company') || '';
  const { data: catalogue } = useGetCentersQuery({ company }, { skip: !company });

  const linkName = catalogue?.find((c) => c.code.toLowerCase() === center.toLowerCase())?.name;
  const own = ownCenters.find((access) => access.code === selectedCenter) ?? ownCenters[0];
  const openOwn = () => {
    const params = new URLSearchParams(searchParams);
    params.set('center', own.code);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  return (
    <Alert
      severity="warning"
      data-testid="labor-no-access"
      action={
        own && (
          <Button color="inherit" size="small" onClick={openOwn}>
            Open {own.name}
          </Button>
        )
      }
    >
      This link is for {linkName ? `${linkName} (${center})` : center}.{' '}
      {own
        ? `Your Labor Management access is for ${ownCenters.map((access) => access.name).join(', ')}.`
        : 'Your profile has no center access.'}{' '}
      To open your center, choose Labor Management in the left menu, then Budget, Projections, or
      Rules.
    </Alert>
  );
}

/**
 * Route-level authorization guard for the Labor Management module (US2).
 *
 * Mounts `children` ONLY once the profile has resolved AND the user has the **LaborManagement**
 * claim AND, when the link names a center, that center is in the profile's Center Access — so an
 * unauthorized user's page shell/data hooks never mount (no partial rendering, no data fetch).
 * Shows a neutral loading state while the profile is unresolved. Unlike `RequireCamEdit`, denial
 * shows a "no access" message in place rather than redirecting, since a shared link's recipient
 * needs to know why the page did not open. A link to a center the user does hold also becomes the
 * facility box's center, so the box and the page never disagree. A link with no `center` renders
 * the child, leaving the page's own "select a facility" state to cover that case.
 */
export function RequireLaborAccess({ children }: { children: ReactElement }): ReactElement | null {
  const { data: profile, isLoading, isError } = useGetMyProfileQuery();
  const [searchParams] = useSearchParams();
  const center = searchParams.get('center');
  const ownCenters: OwnCenter[] = profile?.centerAccess ?? [];
  const linked = findOwnCenter(ownCenters, center);
  useFacilityBoxSync(linked);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="labor-auth-loading" />
      </Box>
    );
  }

  if (isError)
    return (
      <Alert severity="error" data-testid="labor-access-unavailable">
        We could not check your Labor Management access. Please try again.
      </Alert>
    );

  if (!hasLaborManagementAccess(profile))
    return (
      <Alert severity="warning" data-testid="labor-no-access">
        You do not have access to Labor Management.
      </Alert>
    );

  if (center && !linked) return <ForeignCenterNotice center={center} ownCenters={ownCenters} />;

  return children;
}
